import { useEffect, useState } from "react";
import { Routes, Route, useParams, useLocation, useSearchParams, Navigate, useNavigate } from "react-router-dom";
import ModernLandingPage from "./pages/ModernLandingPage";
import SenderPage from "./pages/SenderPage";
import SenderTrackingPage from "./pages/SenderTrackingPage";
import ShopPage from "./pages/ShopPage";
import UnifiedLogin from "./components/auth/UnifiedLogin";
import FrictionlessRegistration from "./components/auth/FrictionlessRegistration";
import ForgotPin from "./components/auth/ForgotPin";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import ModernInboxDashboard from "./pages/ModernInboxDashboard";
import ConfirmReceipt from "./pages/ConfirmReceipt";
import ConfirmSuccess from "./pages/ConfirmSuccess";
import Profile from "./pages/Profile";
import FindShops from "./pages/FindShops";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Help from "./pages/Help";
import AdminPanel from "./pages/AdminPanel";

import RegistrationSuccess from "./pages/RegistrationSuccess";
import DownloadApp from "./pages/DownloadApp";

import ScrollToTop from "./components/ScrollToTop";

import AppLayout from "./layouts/AppLayout";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Network } from "@capacitor/network";
import { useAuth } from "./context/AuthContext";
import { initGA, trackPageView } from "./services/googleAnalytics";

function ShopWrapper() {
  const { shopId } = useParams();
  return <ShopPage shopId={shopId!} />;
}

/**
 * Root Redirect Logic
 * Decides the entry point of the application
 */
function RootRedirect() {
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  
  // Check for shop ID in standard query params
  const shopId = searchParams.get('shop');

  // If loading auth state, wait
  if (loading) return null;

  // PRIORITY: If shop ID is present, we are in SENDER MODE
  if (shopId) {
    return <SenderPage />;
  }

  // Otherwise, follow standard role-based routing
  if (isAuthenticated && user?.is_shopkeeper) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ModernLandingPage />;
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(true);

  // Monitor network status
  useEffect(() => {
    const checkNetwork = async () => {
      try {
        const status = await Network.getStatus();
        setIsOnline(status.connected);
      } catch (e) {
        console.warn('Network status check failed', e);
      }
    };

    checkNetwork();

    const listener = Network.addListener('networkStatusChange', status => {
      setIsOnline(status.connected);
    });

    return () => {
      listener.then(l => l.remove());
    };
  }, []);

  // Handle Android Back Button
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const backListener = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        // Exit app if at root
        CapApp.exitApp();
      }
    });

    return () => {
      backListener.then(l => l.remove());
    };
  }, []);

  // LEGACY HASH REDIRECT: Automatically move users from /#/dashboard to /dashboard
  useEffect(() => {
    if (window.location.hash) {
      const hashPath = window.location.hash.substring(1); // Remove the #
      if (hashPath) {
        console.log('[App] Migrating legacy hash route to clean URL:', hashPath);
        navigate(hashPath, { replace: true });
      }
    }
  }, [navigate]);

  // Initialize Google Analytics
  useEffect(() => {
    initGA();
  }, []);

  // Track page views
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <>
      <ScrollToTop />
      
      {/* Global Offline Banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white text-[10px] font-black uppercase tracking-[0.2em] py-2 text-center shadow-lg">
          No Internet Connection
        </div>
      )}

      <Routes>
        <Route element={<AppLayout />}>
          {/* Main Entry Points */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* SENDER PAGE (Direct access) */}
          <Route path="/send" element={<SenderPage />} />
          
          <Route path="/home" element={<ModernLandingPage />} />
          <Route path="/find-shops" element={<FindShops />} />
          
          {/* Shop and Public routes */}
          <Route path="/shop/:shopId" element={<ShopWrapper />} />
          <Route path="/track" element={<SenderTrackingPage />} />
          <Route path="/confirm" element={<ConfirmReceipt />} />
          <Route path="/confirm-success" element={<ConfirmSuccess />} />

          {/* Authentication */}
          <Route path="/login" element={<UnifiedLogin />} />
          <Route path="/register" element={<FrictionlessRegistration />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />
          <Route path="/forgot-pin" element={<ForgotPin />} />
                
          {/* Protected Shopkeeper Routes */}      
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <ModernInboxDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />

          {/* Secure Admin */}
          <Route
            path="/p-admin-secure-x9k2m7q4"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          
          {/* Info Pages */}
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/help" element={<Help />} />
          <Route path="/download" element={<DownloadApp />} />

          {/* Catch-all fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}
